import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DA_QuantityPicker } from "@molecules/QuantityPicker/QuantityPicker.component";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { DA_BasicLink } from "@danishagro/shared/src/components/atoms/BasicLink/BasicLink.component";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { useScreen } from "@danishagro/shared/src/contexts/screen.context";
import { useNavigate } from "react-router-dom";
import { DA_Text } from "@danishagro/shared/src/components/atoms/Text/Text.component";
import { formatNumber } from "@danishagro/shared/src/helpers/formatNumber.helper";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import {
    DA_NewProduct,
    DA_NewProductSimple,
} from "@danishagro/shared/src/interfaces/productv2.interface";
import { ModalSize, useModal } from "@danishagro/shared/src/contexts/modal.context";
import { DA_DeliveryInfoModalContent } from "@templates/DeliveryInfoModal/DeliveryInfoModal.component";
import { useProductTracker } from "@danishagro/shared/src/gtm/useProductsTracker";
import { DA_InlineLink } from "@danishagro/shared/src/components/atoms/InlineLink/InlineLink.component";
import { B2bImageSrc } from "@danishagro/shared/src/helpers/imageSrc.helper";
import { ImageConfig } from "@danishagro/shared/src/content/imageConfigs/imageConfig.enum";
import { useBottomSheet } from "@danishagro/shared/src/contexts/bottomSheet/bottomSheet.context";
import { DA_QuantityOnPalletOverlay } from "@danishagro/shared/src/modals/QuantityOnPalletShop/Overlay/Overlay.component";
import { DA_Modal } from "@danishagro/shared/src/components/molecules/Modal/Modal.component";
import { modalState } from "@danishagro/shared/src/enums/modalState.enum";
import { BottomSheetDeliveryInfo } from "@organisms/BottomSheetDeliveryInfo";
import { useDynamicOverlay } from "@danishagro/shared/src/hooks/dynamicOverlay/useDynamicOverlay.hook";
import { DA_IconNames } from "@danishagro/shared/src/components/atoms/Icon/Icon.component";
import { getRelativeRootOnLocalhost } from "@danishagro/shared/src/helpers/getRelativeUrlOnLocalhost.helper";
import { B2C_SITE_ORIGIN } from "@danishagro/shared/src/constants/urlRoot.constants";
import { DA_Button } from "@danishagro/shared/src/components/atoms/Button/Button.component";
import { useFavorites } from "@contexts/favorites.context";
import toast from "react-hot-toast";
import { DA_Toast } from "@danishagro/shared/src/components/molecules/Toast/Toast.component";
import { adjustUrl } from "@danishagro/shared/src/helpers/adjustUrl.helper";
import { DA_SalesPriceBase } from "@danishagro/shared/src/interfaces/price.interface";
import { DA_Tag } from "@danishagro/shared/src/components/atoms/Tag/Tag.component";
import { DA_Image } from "@danishagro/shared/src/components/atoms/Image/Image.component";
import { useMaxValue } from "@hooks/useMaxValue.hook";
import { useBasket } from "@danishagro/shared/src/contexts/basket/basket.context";
import { DA_CartButtonB2B } from "@molecules/CartButtonB2B/CartButtonB2B.component";
import { useCustomerPaymentMethod } from "@hooks/useCustomerPaymentMethod.hook";
import { DA_ProductListPrice } from "./components/ProductListPrice/ProductListPrice.component";
import S from "./ProductListItemV1.module.scss";

export type V1Props = DA_NewProductSimple & {
    categoryId?: string; // TODO: DELETE DEBUG DATA
    eagerLoading?: boolean;
    isFavorite?: boolean;

    "data-id"?: string;
    "data-name"?: string;
    "data-listid"?: string;
    "data-price"?: string;
    "data-category"?: string;
    "data-brand"?: string;

    allVariants?: Array<{
        variantId: string;
    }>;
    fetchPriority?: boolean;
};

export const V1 = React.forwardRef<HTMLElement, V1Props>((props, ref) => {
    const {
        hasVariants,
        variantEntityId,
        displayName,
        newProduct,
        imageUrl,
        eagerLoading,
        url,
        hasHazardStatement,
        quantityOnPallet,
    } = props;

    const hasSpecialFreightInformation =
        props.relewiseProduct?.allVariants?.[0]?.data?.specialFreightInformation?.value;
    const specialFreightInformationText =
        props.relewiseProduct?.allVariants?.[0]?.data?.specialFreightDescription?.value;

    const navigate = useNavigate();
    const [quantity, setQuantity] = useState(1);
    const [awaitingResponse, setAwaitingResponse] = useState(false);
    const { currentSite } = useAppData();
    const { showModal } = useModal();
    const { showOverlay } = useDynamicOverlay();
    const { isMobileOrTablet } = useScreen();
    const { displayFavoritesList } = useAppData();
    const { getDictionaryString } = useTranslations();
    const { addToBasket } = useBasket();
    const [choosedValue, setChoosedValue] = useState(false);
    const { presentBottomSheet } = useBottomSheet();
    const [salesEndDate, setSalesEndDate] = useState<DA_SalesPriceBase>(
        props.price?.quantityDiscounts[0]?.discount
    );
    const { siteVariant } = useAppData();
    const [modalViewState, setModalViewState] = useState<modalState>(modalState.hide);
    const [numberOfPallets, setNumberOfPallets] = useState<number>(0);
    const [totalPalletQuantity, setTotalPalletQuantity] = useState<number>(0);

    const isAvailableToBuy = useRef(!props.data?.redirectUrl?.value).current;
    const highMax = useMaxValue(siteVariant);

    // Check for hide price and hide buy button flags from variant data
    const hidePriceField = props.relewiseProduct?.allVariants?.[0]?.data?.hidePrice?.value;
    const hideBuyButtonField = props.relewiseProduct?.allVariants?.[0]?.data?.hideBuyButton?.value;
    const shouldHidePrice = hidePriceField === "Ja";
    const shouldHideBuyButton = hideBuyButtonField === "Ja";

    // Check if current customer can use special checkout
    const { isNotQuickPayOnlyCustomer } = useCustomerPaymentMethod();

    useEffect(() => {
        if (props.price?.quantityDiscounts[0]?.discount)
            setSalesEndDate(props.price?.quantityDiscounts[0]?.discount);
    }, [props.price]);

    const favoritesListUrl = `${getRelativeRootOnLocalhost(B2C_SITE_ORIGIN)}/favorites`;

    const {
        addFavorite,
        removeFavorite,
        isFavoriteProduct,
        loading: favoritesLoading,
    } = useFavorites();

    const productVariantIds =
        props.relewiseProduct?.allVariants?.map((variant) => variant.variantId) ?? [];
    const isFavorite = isFavoriteProduct(
        {
            entityId: props.entityId,
            variantEntityId: props.variantEntityId,
            hasVariants: props.hasVariants,
            variantGroups: [],
        } as DA_NewProduct,
        productVariantIds
    );

    const deliveryModalTitle = getDictionaryString(
        hasSpecialFreightInformation ? "deliveryInfoOverwrite" : "deliveryInfo"
    );

    // Shipping modal
    const openShippingInfoModal = useCallback(() => {
        showModal(
            <DA_DeliveryInfoModalContent
                specialFreightOverlayText={specialFreightInformationText}
            />,
            {
                size: ModalSize.XXL,
            }
        );
    }, [showModal, specialFreightInformationText]);

    const openDynamicOverlay = useCallback(() => {
        showOverlay({
            id: "delivery-filter_open",
            title: deliveryModalTitle,
            content: (
                <BottomSheetDeliveryInfo
                    version="v1"
                    specialFreightOverlayText={specialFreightInformationText}
                />
            ),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showOverlay]);

    const formatPrice = useCallback(
        (price: number) => formatNumber(price, "da-DK", { decimals: 2 }),
        []
    );

    const {
        "data-id": dataId,
        "data-name": dataName,
        "data-listid": dataListId,
        "data-category": dataCategory,
        "data-brand": dataBrand,
        // ... rest of your props
    } = props;

    // Extract the category from the URL
    const category = props?.url?.split("/").slice(0, -1).join("_").replace(/^_/, "");

    // GTM Tracking product clicked
    const trackProductClick = useProductTracker({
        ...props,
        price: formatPrice(props?.price?.priceWithoutVat),
        currency: props?.price?.currency || "DKK",
        quantity: quantity,
        category: category,
        name: displayName,
        brand: dataBrand,
    });

    const latestQuantityRef = useRef(quantity);
    const quantityAsInt = parseInt(quantityOnPallet?.value);

    useEffect(() => {
        latestQuantityRef.current = quantity;
    }, [quantity]);

    const calculateRangeOffset = (quantityOnPallet: number) => {
        return Math.floor(quantityOnPallet * 0.25); // ±25% of quantityOnPallet
    };

    useEffect(() => {
        const intValue = quantity;
        setModalViewState(modalState.hide);

        if (!isNaN(quantity)) {
            const rangeOffset = calculateRangeOffset(quantityAsInt);
            for (let numberOfPallets = 1; numberOfPallets <= 100; numberOfPallets++) {
                const totalPalletQuantity = numberOfPallets * quantityAsInt;
                const lowerBound = totalPalletQuantity - rangeOffset || 0;
                const upperBound = totalPalletQuantity + rangeOffset || 0;

                if (
                    intValue !== totalPalletQuantity &&
                    intValue >= lowerBound &&
                    intValue <= upperBound
                ) {
                    setModalViewState(modalState.show);
                    setNumberOfPallets(numberOfPallets);
                    setTotalPalletQuantity(totalPalletQuantity);
                    return;
                } else if (intValue === totalPalletQuantity) {
                    setModalViewState(modalState.hide);
                    return;
                }
            }
        }
    }, [quantity, quantityAsInt]);

    const check = useCallback(
        (
            showModalState: modalState,
            defaultQuantityOnPallet: number,
            totalPalletQuantity: number,
            numberOfPallets: number
        ) => {
            return new Promise<number>((resolve) => {
                if (showModalState === modalState.show && !choosedValue) {
                    const handleSelection = (selectedValue) => {
                        setQuantity(selectedValue);
                        setChoosedValue(true);
                        resolve(selectedValue); // Resolve the promise after the state is updated with the new quantity
                    };

                    if (isMobileOrTablet) {
                        presentBottomSheet(
                            <DA_QuantityOnPalletOverlay
                                quantity={latestQuantityRef.current}
                                quantityOnPallet={totalPalletQuantity}
                                defaultQuantityOnPallet={defaultQuantityOnPallet}
                                numberOfPallets={numberOfPallets}
                                onSelection={handleSelection}
                            />,
                            {
                                id: "shop-quantity-bottom-sheet",
                                contentPadding: true,
                                headerTitle: getDictionaryString("shopquantityonpallettitle"),
                                snapPoints: ({ minHeight }) => [minHeight],
                                hideCloseButton: true,
                            }
                        );
                    } else {
                        showModal(
                            <DA_Modal title={getDictionaryString("shopquantityonpallettitle")}>
                                <DA_QuantityOnPalletOverlay
                                    quantity={latestQuantityRef.current}
                                    quantityOnPallet={totalPalletQuantity}
                                    defaultQuantityOnPallet={quantityAsInt}
                                    numberOfPallets={numberOfPallets}
                                    onSelection={handleSelection}
                                />
                            </DA_Modal>,
                            {
                                size: ModalSize.XS,
                                shouldCloseOnEsc: false,
                                shouldCloseOnOverlayClick: false,
                            }
                        );
                    }
                } else {
                    resolve(latestQuantityRef.current); // Resolve immediately if no modal is shown
                }
            });
        },
        [
            choosedValue,
            getDictionaryString,
            isMobileOrTablet,
            presentBottomSheet,
            quantityAsInt,
            showModal,
        ]
    );

    // Navigate to the variant that has the lowest discount
    const discountedVariantUrl = useMemo(() => {
        const variantId = props.relewiseProduct?.data?.lowestDiscountVariant?.value;
        if (!variantId) return null;

        const baseUrl = props.relewiseProduct?.data?.canonicalBaseUrl?.value;
        if (!baseUrl) return null;

        const slug = props.relewiseProduct?.allVariants?.find((v) => v.variantId === variantId)
            ?.data?.webNameSlug?.value;

        return slug ? adjustUrl(`${baseUrl}/${slug}`) : null;
    }, [
        props.relewiseProduct?.data?.lowestDiscountVariant?.value,
        props.relewiseProduct?.data?.canonicalBaseUrl?.value,
        props.relewiseProduct?.allVariants,
    ]);

    const productUrl = discountedVariantUrl ?? adjustUrl(url, currentSite);

    const addProductToCart = useCallback(
        async (modalViewState, defaultQuantityOnPallet, totalPalletQuantity, numberOfPallets) => {
            const selectedQuantity = await check(
                modalViewState,
                defaultQuantityOnPallet,
                totalPalletQuantity,
                numberOfPallets
            ); // Wait for check to complete and get the latest quantity

            setAwaitingResponse(true);
            if (hasHazardStatement || hasVariants) {
                navigate(url);
            } else {
                addToBasket(
                    {
                        productId: variantEntityId,
                        quantity: selectedQuantity,
                        productName: dataName || props.displayName,
                    },
                    {
                        showToast: isMobileOrTablet,
                    }
                ).finally(() => setAwaitingResponse(false));
            }
            // GTM Tracking
            trackProductClick("add_to_cart");
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            addToBasket,
            check,
            hasHazardStatement,
            hasVariants,
            navigate,
            trackProductClick,
            url,
            variantEntityId,
            isMobileOrTablet,
        ]
    );

    const onLinkClicked = () => {
        if (typeof window !== "undefined") {
            navigate(`${window.location.pathname}${window.location.search}`, {
                replace: true,
                state: {
                    scrollY: document.documentElement.scrollTop || document.body.scrollTop,
                },
            });
        }
        // // GTM Tracking the product click when the link is clicked
        trackProductClick("select_item");
    };

    // Function to handle adding/removing the item from favorites
    const handleFavoriteToggle = useCallback(() => {
        const action = isFavorite ? removeFavorite : addFavorite;
        const toastText = isFavorite
            ? getDictionaryString("toast.removedFromFavoritesText")
            : getDictionaryString("toast.addedToFavoritesText");

        action(props.entityId, props.variantEntityId).then(() => {
            toast.custom(() => (
                <DA_Toast
                    href={favoritesListUrl}
                    goTo={getDictionaryString("toast.link.showFavorites")}
                    altStyle={true}
                    message={
                        <>
                            <div>{toastText}</div>
                            <div className="break-all">{props.webName}</div>
                        </>
                    }
                />
            ));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFavorite, props.entityId, props.variantEntityId]);

    const calculatePrice = useCallback(() => {
        if (props.price?.quantityDiscounts?.length > 0) {
            const applicableDiscount = props.price.quantityDiscounts.find(
                (item) => quantity >= item.quantityFrom && quantity <= (item.quantityTo || Infinity)
            );
            return applicableDiscount ? applicableDiscount.price : props.price.priceWithoutVat || 0;
        }
        return props.price?.priceWithoutVat || 0;
    }, [props.price, quantity]);

    const calculatedPrice = calculatePrice();

    return (
        <article
            className={cn(S.productListItem, newProduct && S.productListItemNew)}
            ref={ref}
            data-id={dataId}
            data-category={dataCategory}
            data-brand={dataBrand}
            key={variantEntityId}
            data-name={dataName}
            data-listid={dataListId}
            data-price={formatPrice(calculatedPrice)}
        >
            <div className={S.imageWrapper}>
                {props.hasDiscount && (
                    <div className={S.discountTag}>
                        <DA_Tag color="yellow" size="large">
                            {getDictionaryString("general.onSale")}
                        </DA_Tag>
                    </div>
                )}

                <div className={cn(S.favBtn, isFavorite && S.isFavorite)}>
                    {displayFavoritesList ? (
                        hasVariants ? (
                            // If the product has variants, show the link to the favorites list
                            isFavorite ? (
                                <DA_Button
                                    href={favoritesListUrl}
                                    icon={DA_IconNames.HeartSolid}
                                    disabled={favoritesLoading}
                                />
                            ) : (
                                <DA_Button
                                    href={productUrl}
                                    icon={DA_IconNames.HeartOutline}
                                    disabled={favoritesLoading}
                                />
                            )
                        ) : (
                            // If the product does not have variants, show a button to add/remove it directly
                            <DA_Button
                                onClick={handleFavoriteToggle}
                                icon={
                                    isFavorite ? DA_IconNames.HeartSolid : DA_IconNames.HeartOutline
                                }
                                disabled={favoritesLoading}
                            />
                        )
                    ) : null}
                </div>

                <DA_BasicLink href={productUrl} className={S.imageLink} onClick={onLinkClicked}>
                    {
                        <div className={S.productImageWrapper}>
                            {/* TODO: Develop new image component */}
                            <DA_Image
                                src={B2bImageSrc(imageUrl, ImageConfig.CategoryImage)}
                                alt={dataName}
                                loading={eagerLoading ? "eager" : "lazy"}
                                fetchpriority={props.fetchPriority ? "high" : "auto"}
                                className={S.productImage}
                            />
                        </div>
                    }
                </DA_BasicLink>

                {/* {newProduct && (
                        <DA_Badge
                        className={cn(S.badge, S.newProductBadge)}
                        size="normal"
                        text={getDictionaryString("new splat")}
                        />
                    )}

                    {videos && videos.length > 0 ? (
                        <DA_BasicLink href={`${url}#video`} className={S.videoLink}>
                        <DA_Icon style={{ color: "#fff" }} name={DA_IconNames.Play} />
                        </DA_BasicLink>
                    ) : null} */}
            </div>

            <div className={S.productDataWrapper}>
                <div>
                    <div className={S.productNumber}>
                        <DA_Text noPadding smallText>
                            {`${getDictionaryString("product number")} ${props.productNumber}`}
                        </DA_Text>
                    </div>
                    <div className={S.productName}>
                        <DA_BasicLink href={productUrl} className={S.productNameLink}>
                            <span itemProp="name">{displayName}</span>
                        </DA_BasicLink>
                    </div>
                    {hasVariants && (
                        <div className={S.lightText}>{getDictionaryString("more variants")}</div>
                    )}
                </div>

                <div className={S.secondaryContent}>
                    {/** Energy Classification */}
                    {props.data?.energyEfficiencyArrows?.value &&
                        props.data?.energyEfficiencyReport?.value && (
                            <div className={S.energyWrapper}>
                                <DA_BasicLink href={productUrl}>
                                    <img
                                        className={S.energyImage}
                                        src={props.data?.energyEfficiencyArrows?.value}
                                        alt=""
                                    />
                                </DA_BasicLink>
                                <DA_InlineLink
                                    className={S.energyLabel}
                                    href={props.data?.energyEfficiencyReport?.value}
                                    title={getDictionaryString("ProductSheet")}
                                    target="_blank"
                                />
                            </div>
                        )}

                    {/** Price */}

                    {!shouldHidePrice ? (
                        <div>
                            {salesEndDate && Object.entries(salesEndDate).length > 0 && (
                                <DA_Tag color="yellow" size="large">
                                    {getDictionaryString("offer splat")}
                                </DA_Tag>
                            )}

                            {/* TODO: Set up price based on data */}
                            <DA_ProductListPrice
                                quantity={quantity}
                                listPrice={props.listPrice}
                                salesPrice={props.salesPrice}
                                hasVariants={hasVariants}
                                quantityDiscounts={props.data.quantityDiscounts}
                                onClick={
                                    isMobileOrTablet ? openDynamicOverlay : openShippingInfoModal
                                }
                            />
                        </div>
                    ) : (
                        <>
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: getDictionaryString("pdp.contactForPrice"),
                                }}
                            />
                            <span />
                        </>
                    )}

                    <div
                        className={cn(
                            S.pushContent,
                            (shouldHideBuyButton ||
                                (!isNotQuickPayOnlyCustomer && shouldHidePrice)) &&
                                S.pushContentRightAlign
                        )}
                    >
                        {/** Quantity */}
                        {isAvailableToBuy &&
                        !shouldHideBuyButton &&
                        (isNotQuickPayOnlyCustomer || !shouldHidePrice) ? (
                            <DA_QuantityPicker
                                value={quantity}
                                onChange={setQuantity}
                                compactOnMobile
                                higherMax={highMax}
                            />
                        ) : null}

                        {/** Button */}
                        {shouldHideBuyButton || (!isNotQuickPayOnlyCustomer && shouldHidePrice) ? (
                            <DA_CartButtonB2B
                                hasVariants={hasVariants}
                                hasHazardStatements={hasHazardStatement}
                                isAvailableToBuy={false}
                                productPage={productUrl}
                                hideText={isMobileOrTablet}
                                addToCart={() => navigate(adjustUrl(url, currentSite))}
                                showSpinner={false}
                                fullWidth={isMobileOrTablet}
                                className={S.cartSliderBuyBtn}
                            />
                        ) : (
                            <DA_CartButtonB2B
                                hasVariants={hasVariants}
                                hasHazardStatements={hasHazardStatement}
                                isAvailableToBuy={isAvailableToBuy}
                                productPage={productUrl}
                                hideText={isMobileOrTablet}
                                addToCart={() =>
                                    addProductToCart(
                                        modalViewState,
                                        quantityAsInt,
                                        totalPalletQuantity,
                                        numberOfPallets
                                    )
                                }
                                showSpinner={awaitingResponse}
                                fullWidth={isMobileOrTablet}
                                className={S.cartSliderBuyBtn}
                            />
                        )}
                    </div>
                </div>
            </div>
        </article>
    );
});

V1.displayName = "V1";
